/**
 * Purchase Request Form Component
 * Create and edit purchase requests
 */

import React, { useState, useEffect, useRef } from 'react';
import ProcurementDocuments from '../../Common/ProcurementDocuments';
import { useNavigate, useParams } from 'react-router-dom';
import { useDetailBack } from '../../../hooks/useDetailBack';
import { toast } from 'react-hot-toast';
import {
    PlusIcon,
    DocumentIcon,
    CheckCircleIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../../contexts/AuthContext';
import { inventoryAPI } from '../../../services/inventoryApi';
import { assetsApi, assetRegisterApi, budgetApi } from '../../../services/financeApi';
import LoadingSpinner from '../../Common/LoadingSpinner';
import TextInput from '../../Common/TextInput';
import SearchableCostCenterDropdown from '../../Common/SearchableCostCenterDropdown';
import SearchableSelect from '../../Common/SearchableSelect';
import CommonModal from '../../Common/CommonModal';

const PurchaseRequestForm = ({
    open = true,
    onClose,
    onSuccess,
    isModal = false,
    initialData = null,
    viewMode = false
}) => {
    const { user, hasPermission } = useAuth();
    const navigate = useNavigate();
    const handleBack = useDetailBack('/inventory/purchase-requests');
    const { id } = useParams();
    const editId = id || initialData?._id;
    const isEdit = Boolean(editId);
    const docsRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [actionLoading, setActionLoading] = useState('');
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [showRejectionModal, setShowRejectionModal] = useState(false);
    const [approvalData, setApprovalData] = useState({ comments: '' });
    const [rejectionData, setRejectionData] = useState({ reason: '', comments: '' });

    const [formData, setFormData] = useState({
        requestDate: new Date().toISOString().split('T')[0],
        costCenter: '',
        costCenterLabel: '',
        priority: 'normal',
        procurementType: 'INVENTORY',
        lineItems: [
            {
                lineNo: 1,
                item: '', // Add item ID field
                assetId: '',
                serviceId: '',
                assetCode: '',
                assetName: '',
                itemCode: '',
                itemDescription: '',
                uom: '',
                quantityRequested: '',
                requiredByDate: '', // Add required by date field
                preferredSupplier: '',
                preferredSupplierName: '',
                estimatedUnitPrice: '',
                estimatedTotalPrice: '',
                remarks: '',
                specifications: '',
                urgencyReason: ''
            }
        ],
        approvalWorkflow: [],
        budgetInfo: {
            budgetCode: '',
            budgetAmount: '',
            budgetApprovalRequired: false
        },
        internalNotes: []
    });

    const [items, setItems] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [assets, setAssets] = useState([]);
    const [services, setServices] = useState([]); // active service catalog (SERVICE requests)

    useEffect(() => {
        if (editId && isEdit) {
            loadPurchaseRequest();
        } else if (initialData && isEdit) {
            loadInitialData(initialData);
        }
        loadItems();
        loadAssets();
        loadServices();
    }, [editId, initialData]);

    const loadPurchaseRequest = async () => {
        try {
            setLoading(true);
            const response = await inventoryAPI.getPurchaseRequestById(editId);
            const pr = response.data;

            setFormData({
                requestDate: pr.requestDate ? new Date(pr.requestDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                costCenter: pr.costCenter?._id || pr.costCenter || '',
                costCenterLabel: pr.costCenterLabel || '',
                priority: pr.priority || 'normal',
                procurementType: pr.procurementType || 'INVENTORY',
                lineItems: pr.lineItems?.map((item, index) => ({
                    lineNo: item.lineNo || index + 1,
                    item: item.item?._id || item.item || '', // Add item ID field
                    assetId: item.assetId?._id || item.assetId || '',
                    serviceId: item.serviceId?._id || item.serviceId || '',
                    assetCode: item.assetCode || '',
                    assetName: item.assetName || '',
                    itemCode: item.itemCode || '',
                    itemDescription: item.itemDescription || '',
                    uom: item.uom || '',
                    quantityRequested: item.quantityRequested || '',
                    requiredByDate: item.requiredByDate ? new Date(item.requiredByDate).toISOString().split('T')[0] : '', // Add required by date
                    preferredSupplier: item.preferredSupplier?._id || '',
                    preferredSupplierName: item.preferredSupplierName || '',
                    estimatedUnitPrice: item.estimatedUnitPrice || '',
                    estimatedTotalPrice: item.estimatedTotalPrice || '',
                    remarks: item.remarks || '',
                    specifications: item.specifications || '',
                    urgencyReason: item.urgencyReason || ''
                })) || [{
                    lineNo: 1,
                    item: '', // Add item ID field
                    itemCode: '',
                    itemDescription: '',
                    uom: '',
                    quantityRequested: '',
                    requiredByDate: '', // Add required by date
                    preferredSupplier: '',
                    preferredSupplierName: '',
                    estimatedUnitPrice: '',
                    estimatedTotalPrice: '',
                    remarks: '',
                    specifications: '',
                    urgencyReason: ''
                }],
                approvalWorkflow: pr.approvalWorkflow || [],
                budgetInfo: {
                    budgetCode: pr.budgetInfo?.budgetCode || '',
                    budgetAmount: pr.budgetInfo?.budgetAmount || '',
                    budgetApprovalRequired: pr.budgetInfo?.budgetApprovalRequired || false
                },
                internalNotes: pr.internalNotes || []
            });

            // Store status and department separately for approval checking
            setFormData(prevState => ({
                ...prevState,
                _status: pr.status, // Store for approval checking
                _department: pr.department // Store for display
            }));
        } catch (error) {
            console.error('Load purchase request error:', error);
            toast.error('Failed to load purchase request');
            if (onClose) {
                onClose();
            } else {
                handleBack();
            }
        } finally {
            setLoading(false);
        }
    };

    const loadInitialData = (data) => {
        setFormData({
            requestDate: data.requestDate ? new Date(data.requestDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            costCenter: data.costCenter?._id || data.costCenter || '',
            costCenterLabel: data.costCenterLabel || '',
            priority: data.priority || 'normal',
            procurementType: data.procurementType || 'INVENTORY',
            lineItems: data.lineItems?.map((item, index) => ({
                lineNo: item.lineNo || index + 1,
                item: item.item?._id || item.item || '',
                assetId: item.assetId?._id || item.assetId || '',
                serviceId: item.serviceId?._id || item.serviceId || '',
                assetCode: item.assetCode || '',
                assetName: item.assetName || '',
                itemCode: item.itemCode || '',
                itemDescription: item.itemDescription || '',
                uom: item.uom || '',
                quantityRequested: item.quantityRequested || '',
                requiredByDate: item.requiredByDate ? new Date(item.requiredByDate).toISOString().split('T')[0] : '', // Add required by date
                preferredSupplier: item.preferredSupplier?._id || '',
                preferredSupplierName: item.preferredSupplierName || '',
                estimatedUnitPrice: item.estimatedUnitPrice || '',
                estimatedTotalPrice: item.estimatedTotalPrice || '',
                remarks: item.remarks || '',
                specifications: item.specifications || '',
                urgencyReason: item.urgencyReason || ''
            })) || formData.lineItems,
            approvalWorkflow: data.approvalWorkflow || [],
            budgetInfo: {
                budgetCode: data.budgetInfo?.budgetCode || '',
                budgetAmount: data.budgetInfo?.budgetAmount || '',
                budgetApprovalRequired: data.budgetInfo?.budgetApprovalRequired || false
            },
            internalNotes: data.internalNotes || [],
            // Store status and department separately for approval checking
            _status: data.status,
            _department: data.department
        });
    };

    const loadServices = async () => {
        try {
            const response = await inventoryAPI.getServices({ isActive: 'true', limit: 1000, sortBy: 'name', sortOrder: 'asc' });
            setServices(response.data || []);
        } catch (error) {
            console.error('Error loading services:', error);
        }
    };

    const loadItems = async () => {
        try {
            const response = await inventoryAPI.getItems({ limit: 1000 });
            setItems(response.data || []);
            setFilteredItems(response.data || []);
        } catch (error) {
            console.error('Error loading items:', error);
        }
    };

    const loadAssets = async () => {
        try {
            const response = await assetsApi.getAll({ status: 'ACTIVE', limit: 200 });
            setAssets(response.data?.data || response.data || []);
        } catch (error) {
            console.error('Error loading fixed assets:', error);
            setAssets([]);
        }
    };


    // ── Budget of each line's account under the PR's cost center: an item's expense account, a fixed
    // asset's cost ledger (capital budget) ──
    const [budgetByAccount, setBudgetByAccount] = useState({}); // accountId -> { loading | error | availableAmount, budgetedAmount, utilizedAmount, bypassedValidation }
    const [assetCostAccounts, setAssetCostAccounts] = useState({}); // assetId -> { accountId, code, name, group }
    const assetIdsKey = formData.procurementType === 'FIXED_ASSET'
        ? [...new Set(formData.lineItems.map(l => l.assetId).filter(Boolean).map(String))].sort().join(',')
        : '';
    useEffect(() => {
        const missing = assetIdsKey ? assetIdsKey.split(',').filter(id => !assetCostAccounts[id]) : [];
        if (!missing.length) return undefined;
        let cancelled = false;
        assetRegisterApi.costAccounts(missing)
            .then(res => { if (!cancelled) setAssetCostAccounts(prev => ({ ...prev, ...(res.data?.data || {}) })); })
            .catch(() => { if (!cancelled) setAssetCostAccounts(prev => ({ ...prev, ...Object.fromEntries(missing.map(id => [id, { accountId: null }])) })); });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetIdsKey]);
    const [serviceAccounts, setServiceAccounts] = useState({}); // serviceId -> { accountId, label, source } | null
    const serviceIdsKey = formData.procurementType === 'SERVICE'
        ? [...new Set(formData.lineItems.map(l => l.serviceId).filter(Boolean).map(String))].sort().join(',')
        : '';
    useEffect(() => {
        const missing = serviceIdsKey ? serviceIdsKey.split(',').filter(id => !(id in serviceAccounts)) : [];
        if (!missing.length) return undefined;
        let cancelled = false;
        inventoryAPI.getServiceExpenseAccounts(missing)
            .then(res => { if (!cancelled) setServiceAccounts(prev => ({ ...prev, ...(res.data || {}) })); })
            .catch(() => { if (!cancelled) setServiceAccounts(prev => ({ ...prev, ...Object.fromEntries(missing.map(id => [id, null])) })); });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceIdsKey]);
    const budgetAccountOf = (line) => {
        if (formData.procurementType === 'SERVICE') {
            const acc = line?.serviceId ? serviceAccounts[String(line.serviceId)] : null;
            return acc?.accountId ? { id: String(acc.accountId), label: acc.label + (acc.source === 'mapping' ? ' (Service Completion mapping)' : '') } : null;
        }
        if (formData.procurementType === 'FIXED_ASSET') {
            const acc = line?.assetId ? assetCostAccounts[String(line.assetId)] : null;
            return acc?.accountId ? { id: String(acc.accountId), label: [acc.code, acc.name].filter(Boolean).join(' ') } : null;
        }
        if (!line?.item) return null;
        const it = items.find(i => String(i._id) === String(line.item));
        const acc = it?.accounting?.expenseAccount;
        return acc?.accountId ? { id: String(acc.accountId), label: acc.label || '' } : null;
    };
    const budgetAccountKey = [...new Set(formData.lineItems.map(l => budgetAccountOf(l)?.id).filter(Boolean))].sort().join(',');
    useEffect(() => {
        if (!formData.costCenter || !budgetAccountKey) { setBudgetByAccount({}); return undefined; }
        let cancelled = false;
        const ids = budgetAccountKey.split(',');
        setBudgetByAccount(Object.fromEntries(ids.map(id => [id, { loading: true }])));
        Promise.all(ids.map(id => budgetApi.checkBudgetAvailability({ costCenter: formData.costCenter, ledgerAccountId: id, amount: 0 })
            .then(res => [id, res.data?.data || {}])
            .catch(err => [id, { error: err.response?.data?.message || 'No budget available' }])))
            .then(rows => { if (!cancelled) setBudgetByAccount(Object.fromEntries(rows)); });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.costCenter, budgetAccountKey]);
    // Budget this saved PR already holds (reserved at save) — per ledger, for its cost center
    const [reservedByThisPr, setReservedByThisPr] = useState({});
    useEffect(() => {
        if (!editId) { setReservedByThisPr({}); return undefined; }
        let cancelled = false;
        budgetApi.getDocumentReservation(editId)
            .then(res => {
                if (cancelled) return;
                const map = {};
                (res.data?.data?.lines || []).forEach(l => {
                    if (String(l.costCenter) === String(formData.costCenter)) map[String(l.ledgerId)] = (map[String(l.ledgerId)] || 0) + (l.reserved || 0);
                });
                setReservedByThisPr(map);
            })
            .catch(() => { if (!cancelled) setReservedByThisPr({}); });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editId, formData.costCenter, budgetAccountKey]);

    // What this PR asks for on each budget account (all its lines together)
    const requestedByAccount = formData.lineItems.reduce((acc, l) => {
        const a = budgetAccountOf(l);
        if (a) acc[a.id] = (acc[a.id] || 0) + (parseFloat(l.quantityRequested) || 0) * (parseFloat(l.estimatedUnitPrice) || 0);
        return acc;
    }, {});
    const budgetMoney = (n) => `Rs ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    // Inventory requests choose from all items (suppliers quote later — see Supplier Quotations)
    useEffect(() => {
        setFilteredItems(formData.procurementType === 'FIXED_ASSET' ? [] : items);
    }, [formData.procurementType, items]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const handleLineItemChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            lineItems: prev.lineItems.map((item, i) => {
                if (i === index) {
                    const updatedItem = { ...item, [field]: value };

                    // Auto-calculate total price when unit price or quantity changes
                    if (field === 'estimatedUnitPrice' || field === 'quantityRequested') {
                        const unitPrice = field === 'estimatedUnitPrice' ? parseFloat(value) || 0 : parseFloat(item.estimatedUnitPrice) || 0;
                        const quantity = field === 'quantityRequested' ? parseFloat(value) || 0 : parseFloat(item.quantityRequested) || 0;
                        updatedItem.estimatedTotalPrice = unitPrice * quantity;
                    }

                    return updatedItem;
                }
                return item;
            })
        }));

        // Clear error for this line item field
        if (errors[`lineItems.${index}.${field}`]) {
            setErrors(prev => ({
                ...prev,
                [`lineItems.${index}.${field}`]: null
            }));
        }
    };

    const addLineItem = () => {
        const newLineNo = Math.max(...formData.lineItems.map(item => item.lineNo), 0) + 1;

        setFormData(prev => ({
            ...prev,
            lineItems: [
                ...prev.lineItems,
                {
                    lineNo: newLineNo,
                    item: '', // Add item ID field
                    assetId: '',
                    serviceId: '',
                    assetCode: '',
                    assetName: '',
                    itemCode: '',
                    itemDescription: '',
                    uom: '',
                    quantityRequested: '',
                    preferredSupplier: '',
                    preferredSupplierName: '',
                    estimatedUnitPrice: '',
                    estimatedTotalPrice: '',
                    remarks: '',
                    specifications: '',
                    urgencyReason: ''
                }
            ]
        }));
    };

    const removeLineItem = (index) => {
        if (formData.lineItems.length > 1) {
            setFormData(prev => ({
                ...prev,
                lineItems: prev.lineItems.filter((_, i) => i !== index)
            }));
        }
    };

    const handleItemSelection = (index, itemId) => {
        // setFormData(prev => ({
        //     ...prev,
        //     items: prev.items.map((item, i) => {
        //         if (i === index) {
        //             const updatedItem = { ...item, [field]: value };

        //             // Auto-populate price and unit when item is selected
        //             if (field === 'item') {
        //                 const selectedItem = items.find(item => item._id === value);
        //                 if (selectedItem) {
        //                     updatedItem.unitPrice = selectedItem.pricing?.sellingPrice || selectedItem.pricing?.unitCost || 0;
        //                     updatedItem.unit = selectedItem.inventory?.unit || selectedItem.unitOfMeasure || 'PCS';
        //                 }
        //             }

        //             return updatedItem;
        //         }
        //         return item;
        //     })
        //.............
        setFormData(prev => ({
            ...prev,
            lineItems: prev.lineItems.map((lineItem, i) => {
                if (i === index) {
                    // Item selected - auto-fill fields
                    const selectedItem = filteredItems.find(item => item._id === itemId);
                    const unitPrice = selectedItem ? (selectedItem.pricing?.sellingPrice || selectedItem.pricing?.unitCost || 0) : 0;
                    const quantity = parseFloat(lineItem.quantityRequested) || 0;

                    return {
                        ...lineItem,
                        item: itemId,
                        itemCode: selectedItem ? selectedItem.sku : '',
                        itemDescription: selectedItem ? selectedItem.name : '',
                        uom: selectedItem ? (selectedItem.inventory?.unit || selectedItem.unitOfMeasure || 'PCS') : '',
                        estimatedUnitPrice: unitPrice,
                        estimatedTotalPrice: unitPrice * quantity
                    };
                }
                return lineItem;
            })
        }));
    };

    const handleAssetSelection = (index, assetId) => {
        setFormData(prev => ({
            ...prev,
            lineItems: prev.lineItems.map((lineItem, i) => {
                if (i === index) {
                    const selectedAsset = assets.find(a => a._id === assetId);
                    const unitPrice = selectedAsset ? (selectedAsset.purchasePrice || selectedAsset.marketPrice || 0) : 0;
                    const quantity = parseFloat(lineItem.quantityRequested) || 0;
                    return {
                        ...lineItem,
                        assetId,
                        assetCode: selectedAsset ? (selectedAsset.code || '') : '',
                        assetName: selectedAsset ? (selectedAsset.name || '') : '',
                        itemDescription: selectedAsset ? (selectedAsset.name || '') : lineItem.itemDescription,
                        uom: lineItem.uom || 'PCS',
                        estimatedUnitPrice: unitPrice,
                        estimatedTotalPrice: unitPrice * quantity
                    };
                }
                return lineItem;
            })
        }));
    };

    const handleServiceSelection = (index, serviceId) => {
        const svc = services.find(x => String(x._id) === String(serviceId));
        setFormData(prev => ({
            ...prev,
            lineItems: prev.lineItems.map((lineItem, i) => (i !== index ? lineItem : {
                ...lineItem,
                serviceId,
                itemCode: svc?.code || '',
                itemDescription: svc?.name || '',
                uom: svc?.uom || lineItem.uom || 'JOB',
            }))
        }));
        if (errors[`lineItems.${index}.item`]) setErrors(prev => ({ ...prev, [`lineItems.${index}.item`]: null }));
    };

    const handleProcurementTypeChange = (value) => {
        setFormData(prev => ({
            ...prev,
            procurementType: value,
            // Reset line refs when switching type
            lineItems: prev.lineItems.map(li => ({
                ...li,
                item: '',
                assetId: '',
                serviceId: '',
                assetCode: '',
                assetName: '',
                itemCode: '',
                itemDescription: ''
            }))
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        // No supplier on a purchase request — it is decided by the selected supplier quotation

        if (!formData.costCenter) {
            newErrors.costCenter = 'Cost center is required';
        }

        // Validate line items
        const isFixedAsset = formData.procurementType === 'FIXED_ASSET';
        const isService = formData.procurementType === 'SERVICE';
        formData.lineItems.forEach((item, index) => {
            if (isService) {
                if (!item.serviceId) newErrors[`lineItems.${index}.item`] = 'Please select a service';
            } else if (isFixedAsset) {
                // Fixed-asset PRs must reference an asset
                if (!item.assetId) {
                    newErrors[`lineItems.${index}.item`] = 'Please select a fixed asset';
                }
            } else if (!item.item && !item.itemDescription.trim()) {
                // Check if either an item is selected OR manual description is provided
                newErrors[`lineItems.${index}.item`] = 'Please select an item or enter item description';
            }

            // UOM is required but might be auto-filled from item selection
            if (!item.uom || !item.uom.trim()) {
                newErrors[`lineItems.${index}.uom`] = 'Unit of measure is required';
            }

            // Quantity is always required
            if (!item.quantityRequested || parseFloat(item.quantityRequested) <= 0) {
                newErrors[`lineItems.${index}.quantityRequested`] = 'Valid quantity is required';
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Please fix validation errors');
            return;
        }

        try {
            setSubmitting(true);

            // Build a clean payload — only DB fields, no UI-only labels
            const submitData = {
                requestDate: formData.requestDate,
                priority: formData.priority,
                procurementType: formData.procurementType,
                costCenter: formData.costCenter,
                costCenterLabel: formData.costCenterLabel || '',
                approvalWorkflow: formData.approvalWorkflow,
                internalNotes: formData.internalNotes,
                budgetInfo: {
                    budgetCode: formData.budgetInfo.budgetCode || '',
                    budgetApprovalRequired: formData.budgetInfo.budgetApprovalRequired || false,
                    ...(formData.budgetInfo.budgetAmount !== '' && formData.budgetInfo.budgetAmount != null
                        ? { budgetAmount: parseFloat(formData.budgetInfo.budgetAmount) }
                        : {})
                },
                lineItems: formData.lineItems.map(item => {
                    const line = {
                        lineNo: item.lineNo,
                        itemDescription: item.itemDescription,
                        uom: item.uom,
                        quantityRequested: parseFloat(item.quantityRequested),
                        estimatedUnitPrice: parseFloat(item.estimatedUnitPrice) || 0,
                        estimatedTotalPrice: parseFloat(item.estimatedTotalPrice) || 0,
                        remarks: item.remarks || '',
                        specifications: item.specifications || '',
                        urgencyReason: item.urgencyReason || '',
                        preferredSupplierName: item.preferredSupplierName || ''
                    };
                    if (item.requiredByDate) line.requiredByDate = new Date(item.requiredByDate);
                    if (item.item) line.item = item.item;
                    if (item.itemCode) line.itemCode = item.itemCode;
                    if (item.assetId) line.assetId = item.assetId;
                    if (item.assetCode) line.assetCode = item.assetCode;
                    if (item.assetName) line.assetName = item.assetName;
                    if (item.serviceId) line.serviceId = item.serviceId;
                    if (item.preferredSupplier) line.preferredSupplier = item.preferredSupplier;
                    return line;
                })
            };


            let response;
            if (isEdit) {
                response = await inventoryAPI.updatePurchaseRequest(editId, submitData);
            } else {
                response = await inventoryAPI.createPurchaseRequest(submitData);
                await docsRef.current?.uploadPending(response.data?._id);
            }

            if (onSuccess) {
                onSuccess(response.data);
            } else {
                // Opened as its own page (/new, /edit/:id): back to the list, which opens the saved request
                toast.success(`Purchase request ${response.data?.prNumber || ''} ${isEdit ? 'updated' : 'created'}`);
                navigate('/inventory/purchase-requests', { state: { openPRId: response.data._id } });
            }
        } catch (error) {
            console.error('Submit purchase request error:', error);
            toast.error(error.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} purchase request`);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        if (onClose) {
            onClose();
        } else {
            handleBack();
        }
    };

    const calculateTotalEstimatedAmount = () => {
        return formData.lineItems.reduce((sum, item) => {
            return sum + (parseFloat(item.estimatedTotalPrice) || 0);
        }, 0);
    };

    const canApprove = () => {
        // Get the current PR data - prioritize formData if available (loaded), otherwise use initialData
        const currentData = formData.approvalWorkflow?.length > 0 ? formData : initialData;
        const currentStatus = formData._status || initialData?.status;
        const hasPermissionCheck = hasPermission('INVENTORY:PURCHASE_REQUESTS:APPROVE');
        const statusCheck = ['submitted', 'pending_approval', 'pending'].includes(currentStatus);
        // const workflowCheck = currentData?.approvalWorkflow?.some(approval =>
        //     approval.approver &&
        //     (approval.approver._id === user.id || approval.approver === user.id) &&
        //     approval.status === 'pending'
        // );
        return hasPermissionCheck && statusCheck;
    };

    const handleApprove = async () => {
        try {
            setActionLoading('approve');
            await inventoryAPI.approvePurchaseRequest(editId, approvalData);
            toast.success('Purchase request approved successfully');
            setShowApprovalModal(false);
            setApprovalData({ comments: '' });
            if (onSuccess) {
                onSuccess();
            }
            // Close the view modal after approval
            if (onClose) {
                onClose();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to approve purchase request');
        } finally {
            setActionLoading('');
        }
    };

    const handleReject = async () => {
        try {
            setActionLoading('reject');
            await inventoryAPI.rejectPurchaseRequest(editId, rejectionData);
            toast.success('Purchase request rejected');
            setShowRejectionModal(false);
            setRejectionData({ reason: '', comments: '' });
            if (onSuccess) {
                onSuccess();
            }
            // Close the view modal after rejection
            if (onClose) {
                onClose();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to reject purchase request');
        } finally {
            setActionLoading('');
        }
    };

    if (loading) {
        if (isModal && open) {
            return (
                <div className="fixed inset-0 backdrop-blur-sm bg-black/40 overflow-y-auto z-50 flex items-center justify-center p-4">
                    <div className="relative w-full max-w-6xl max-h-[90vh] bg-white shadow-2xl border-0 flex flex-col rounded-2xl overflow-hidden">
                        <div className="flex-shrink-0 bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                                        <DocumentIcon className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold text-white">
                                            {isEdit ? 'Edit Purchase Request' : 'New Purchase Request'}
                                        </h3>
                                        <p className="text-indigo-100 text-sm mt-1">
                                            Capture purchase requirements and route them for approval
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="w-10 h-10 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
                                >
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6">
                            <LoadingSpinner />
                        </div>
                    </div>
                </div>
            );
        }
        return <LoadingSpinner />;
    }

    const formContent = (
        <div>
            <form onSubmit={handleSubmit} className="px-4 pt-3 pb-2">

                {/* Header Fields */}
                <div className="grid grid-cols-4 gap-2 mb-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            Procurement Type <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={formData.procurementType}
                            onChange={(e) => handleProcurementTypeChange(e.target.value)}
                            disabled={viewMode}
                            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                            <option value="INVENTORY">Inventory</option>
                            <option value="FIXED_ASSET">Fixed Asset</option>
                            <option value="SERVICE">Service</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            Request Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            value={formData.requestDate}
                            onChange={(e) => handleInputChange('requestDate', e.target.value)}
                            disabled={viewMode}
                            className={`w-full px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${errors.requestDate ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                        />
                        {errors.requestDate && <p className="text-xs text-red-500 mt-0.5">{errors.requestDate}</p>}
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Cost Center <span className="text-red-500">*</span></label>
                        <SearchableCostCenterDropdown
                            value={formData.costCenter}
                            displayLabel={formData.costCenterLabel}
                            onChange={(id, label) => {
                                handleInputChange('costCenter', id);
                                handleInputChange('costCenterLabel', label || '');
                                if (errors.costCenter) setErrors(prev => ({ ...prev, costCenter: null }));
                            }}
                            placeholder="Select cost center"
                            disabled={viewMode}
                            error={!!errors.costCenter}
                            limit={50}
                            menuPlacement="down"
                        />
                        {errors.costCenter && <p className="text-xs text-red-500 mt-0.5">{errors.costCenter}</p>}
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                        <select
                            value={formData.priority}
                            onChange={(e) => handleInputChange('priority', e.target.value)}
                            disabled={viewMode}
                            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                            <option value="normal">Normal</option>
                            <option value="urgent">Urgent</option>
                            <option value="critical">Critical</option>
                        </select>
                    </div>

                </div>

                {/* Line Items */}
                <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden">
                    <div className="flex items-center justify-between bg-gradient-to-r from-indigo-600 to-purple-600 px-3 py-1.5">
                        <span className="text-xs font-semibold text-white">Line Items ({formData.lineItems.length})</span>
                        {!viewMode && (
                            <button
                                type="button"
                                onClick={addLineItem}
                                className="flex items-center gap-1 px-2 py-0.5 bg-white/20 hover:bg-white/30 rounded text-xs text-white transition-colors"
                            >
                                <PlusIcon className="w-3 h-3" />
                                Add
                            </button>
                        )}
                    </div>

                    <div className="max-h-[44vh] overflow-y-auto divide-y divide-gray-100">
                        {formData.lineItems.map((item, index) => (
                            <div key={index} className="px-3 py-2 hover:bg-gray-50/60 transition-colors relative">
                                {!viewMode && formData.lineItems.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeLineItem(index)}
                                        className="absolute right-1 top-2 w-4 h-4 flex items-center justify-center text-gray-300 hover:text-red-500 transition-colors"
                                        title="Remove"
                                    >
                                        <XMarkIcon className="w-3.5 h-3.5" />
                                    </button>
                                )}

                                {/* Row 1: main fields */}
                                <div className="grid grid-cols-[2.2fr_0.55fr_0.7fr_0.9fr_0.75fr_0.85fr] gap-2 pr-5 mb-1.5">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">
                                            {formData.procurementType === 'FIXED_ASSET' ? 'Fixed Asset' : formData.procurementType === 'SERVICE' ? 'Service' : 'Item'} <span className="text-red-500">*</span>
                                        </label>
                                        {formData.procurementType === 'SERVICE' ? (
                                            <SearchableSelect
                                                value={item.serviceId}
                                                onChange={(e) => handleServiceSelection(index, e.target.value)}
                                                options={services.map(x => ({ value: x._id, label: `${x.name} (${x.code})` }))}
                                                placeholder={services.length === 0 ? 'No services — add them in Inventory → Services' : 'Select service…'}
                                                disabled={viewMode}
                                                error={errors[`lineItems.${index}.item`] || ''}
                                                size="sm"
                                            />
                                        ) : formData.procurementType === 'FIXED_ASSET' ? (
                                            <SearchableSelect
                                                value={item.assetId}
                                                onChange={(e) => handleAssetSelection(index, e.target.value)}
                                                options={assets.map(a => ({ value: a._id, label: `${a.name}${a.code ? ` (${a.code})` : ''}${a.serialNumber ? ` — ${a.serialNumber}` : ''}` }))}
                                                placeholder={assets.length === 0 ? 'No fixed assets available' : 'Select asset…'}
                                                disabled={viewMode}
                                                error={errors[`lineItems.${index}.item`] || ''}
                                                size="sm"
                                            />
                                        ) : (
                                            <SearchableSelect
                                                value={item.item}
                                                onChange={(e) => handleItemSelection(index, e.target.value)}
                                                options={filteredItems.map(i => ({ value: i._id, label: `${i.name} (${i.sku})` }))}
                                                placeholder="Select item…"
                                                disabled={viewMode}
                                                error={errors[`lineItems.${index}.item`] || ''}
                                                size="sm"
                                            />
                                        )}
                                        {errors[`lineItems.${index}.item`] && <p className="text-[10px] text-red-500 mt-0.5">{errors[`lineItems.${index}.item`]}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">UoM <span className="text-red-500">*</span></label>
                                        <input
                                            type="text"
                                            value={item.uom}
                                            onChange={(e) => handleLineItemChange(index, 'uom', e.target.value)}
                                            placeholder="PCS"
                                            disabled={viewMode || (item.item && item.item !== '')}
                                            className={`w-full px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${errors[`lineItems.${index}.uom`] ? 'border-red-300 bg-red-50' : 'border-gray-300'} ${(item.item && item.item !== '') ? 'bg-gray-100' : ''}`}
                                        />
                                        {errors[`lineItems.${index}.uom`] && <p className="text-[10px] text-red-500 mt-0.5">{errors[`lineItems.${index}.uom`]}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Qty <span className="text-red-500">*</span></label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={item.quantityRequested}
                                            onChange={(e) => handleLineItemChange(index, 'quantityRequested', e.target.value)}
                                            placeholder="0.00"
                                            disabled={viewMode}
                                            className={`w-full px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-right ${errors[`lineItems.${index}.quantityRequested`] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                        />
                                        {errors[`lineItems.${index}.quantityRequested`] && <p className="text-[10px] text-red-500 mt-0.5">{errors[`lineItems.${index}.quantityRequested`]}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">{formData.procurementType === 'SERVICE' ? 'Service Required By' : 'Required By'}</label>
                                        <input
                                            type="date"
                                            value={item.requiredByDate}
                                            onChange={(e) => handleLineItemChange(index, 'requiredByDate', e.target.value)}
                                            disabled={viewMode}
                                            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Unit Price</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={item.estimatedUnitPrice}
                                            onChange={(e) => handleLineItemChange(index, 'estimatedUnitPrice', e.target.value)}
                                            placeholder="0.00"
                                            disabled={viewMode}
                                            className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-right ${viewMode ? 'bg-gray-100' : 'bg-white'}`}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Net Amount</label>
                                        <div className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-gray-50 text-right font-medium text-gray-700">
                                            {(parseFloat(item.quantityRequested || 0) * parseFloat(item.estimatedUnitPrice || 0)).toFixed(2)}
                                        </div>
                                    </div>
                                </div>

                                {/* Row 2: secondary details strip */}
                                <div className="grid grid-cols-[0.9fr_2fr] gap-2 pr-5">
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-[10px] text-gray-400 whitespace-nowrap">Item Code:</span>
                                        <span className="text-[10px] font-medium text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded truncate">
                                            {item.itemCode || '—'}
                                        </span>
                                    </div>
                                    <div>
                                        <input
                                            type="text"
                                            value={item.remarks}
                                            onChange={(e) => handleLineItemChange(index, 'remarks', e.target.value)}
                                            placeholder="Remarks / notes for this line…"
                                            disabled={viewMode}
                                            className="w-full px-2 py-0.5 text-[10px] border border-gray-200 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50 placeholder-gray-400"
                                        />
                                    </div>
                                </div>

                                {/* Row 3: available budget of the item's expense account / the asset's cost ledger
                                    (capital budget), cost center of the PR */}
                                {(formData.procurementType === 'FIXED_ASSET' ? item.assetId : formData.procurementType === 'SERVICE' ? item.serviceId : item.item) && (() => {
                                    const isAsset = formData.procurementType === 'FIXED_ASSET';
                                    const isService = formData.procurementType === 'SERVICE';
                                    const acc = budgetAccountOf(item);
                                    const strip = 'mt-1 mr-5 px-2 py-1 rounded text-[10px] flex flex-wrap items-center gap-x-2';
                                    if (isAsset && !assetCostAccounts[String(item.assetId)]) return <p className={`${strip} bg-gray-50 text-gray-500`}>Capital budget: loading…</p>;
                                    if (isService && !(String(item.serviceId) in serviceAccounts)) return <p className={`${strip} bg-gray-50 text-gray-500`}>Budget: loading…</p>;
                                    if (!acc) return (
                                        <p className={`${strip} bg-amber-50 text-amber-800`} data-testid="no-budget-ledger">
                                            {isService
                                                ? 'This service has no expense account and no Service Completion account is set in Transaction Ledger Mapping — its budget cannot be checked, so the request will be refused while the budget check is on.'
                                                : isAsset
                                                ? 'This asset\'s group has no asset cost ledger — its capital budget cannot be checked, so the request will be refused while the budget check is on. Set the ledger on the asset group.'
                                                : 'This item has no expense account — its budget cannot be checked, so the request will be refused while the budget check is on. Set the expense account on the item.'}
                                        </p>
                                    );
                                    if (!formData.costCenter) return <p className={`${strip} bg-gray-50 text-gray-500`}>Budget · {acc.label}: select the cost center to see the available budget</p>;
                                    const b = budgetByAccount[acc.id];
                                    if (!b || b.loading) return <p className={`${strip} bg-gray-50 text-gray-500`}>Budget · {acc.label}: loading…</p>;
                                    if (b.error) return <p className={`${strip} bg-red-50 text-red-700`}>Budget · {acc.label}: {b.error}</p>;
                                    if (b.bypassedValidation && b.availableAmount == null) return <p className={`${strip} bg-gray-50 text-gray-500`}>Budget · {acc.label}: no budget allocation (budget validation is off)</p>;
                                    const requested = requestedByAccount[acc.id] || 0;
                                    const ownReserved = reservedByThisPr[acc.id] || 0;
                                    const over = requested - ((b.availableAmount || 0) + ownReserved);
                                    return (
                                        <p className={`${strip} ${over > 0.005 ? 'bg-amber-50 text-amber-800' : 'bg-green-50 text-green-800'}`}>
                                            <span>{isAsset ? 'Capital budget' : 'Budget'} · <span className="font-medium">{acc.label}</span>:</span>
                                            <span>available <span className="font-semibold">{budgetMoney(b.availableAmount)}</span> of {budgetMoney(b.budgetedAmount)}</span>
                                            {b.utilizedAmount != null && <span className="text-gray-500">(used {budgetMoney(b.utilizedAmount)})</span>}
                                            {ownReserved > 0 && <span className="text-indigo-700">· reserved by this PR {budgetMoney(ownReserved)}</span>}
                                            {over > 0.005 && <span className="font-semibold">· this request needs {budgetMoney(requested)} — {budgetMoney(over)} over</span>}
                                        </p>
                                    );
                                })()}
                            </div>
                        ))}
                    </div>
                    {errors.lineItems && <p className="text-xs text-red-500 px-3 py-1.5 border-t border-gray-100">{errors.lineItems}</p>}
                </div>

                {/* Supporting documents */}
                <ProcurementDocuments ref={docsRef} docType="purchase-requests" docId={isEdit ? editId : null} className="mb-3" />

                {/* Footer: Total + Actions */}
                <div className="flex items-end justify-between gap-4 pt-2 border-t border-gray-100">
                    <div className="w-56 border border-gray-200 rounded-lg overflow-hidden">
                        <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100 bg-gray-50">
                            <span className="text-xs text-gray-500">Total Items</span>
                            <span className="text-xs font-semibold text-gray-700">{formData.lineItems.length}</span>
                        </div>
                        <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-indigo-600 to-purple-600">
                            <span className="text-[10px] font-bold text-indigo-100 uppercase tracking-wider">Est. Total</span>
                            <span className="text-sm font-black text-white">Rs {calculateTotalEstimatedAmount().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {viewMode ? (
                            canApprove() && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => setShowRejectionModal(true)}
                                        className="px-3 py-1.5 text-xs font-medium border border-red-300 text-red-700 bg-white hover:bg-red-50 rounded transition-colors"
                                    >
                                        <XMarkIcon className="w-3 h-3 mr-1 inline" />
                                        Reject
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowApprovalModal(true)}
                                        className="px-3 py-1.5 text-xs font-medium border border-green-300 text-green-700 bg-white hover:bg-green-50 rounded transition-colors"
                                    >
                                        <CheckCircleIcon className="w-3 h-3 mr-1 inline" />
                                        Approve
                                    </button>
                                </>
                            )
                        ) : (
                            <>
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {submitting && (
                                        <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                        </svg>
                                    )}
                                    {isEdit ? 'Update Purchase Request' : 'Create Purchase Request'}
                                </button>
                            </>
                        )}
                    </div>
                </div>

            </form>
        </div>
    );

    // Return appropriate wrapper based on mode
    if (isModal) {
        if (!open) return null;
        return (
            <div className="fixed inset-0 backdrop-blur-sm bg-black/40 overflow-y-auto z-50 flex items-center justify-center p-4">
                <div className="relative w-full max-w-6xl max-h-[90vh] bg-white shadow-2xl border-0 flex flex-col rounded-2xl overflow-hidden">
                    {/* Header */}
                    <div className="flex-shrink-0 bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                                    <DocumentIcon className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold text-white">
                                        {viewMode ? 'View Purchase Request' : (isEdit ? 'Edit Purchase Request' : 'New Purchase Request')}
                                    </h3>
                                    <p className="text-indigo-100 text-sm mt-1">
                                        Capture purchase requirements and route them for approval
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="w-10 h-10 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
                            >
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Body */}
                    <div className="flex-1 overflow-y-auto">
                        {formContent}

                {/* Approval Modal */}
                {showApprovalModal && (
                    <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-[60]">
                        <div className="bg-white p-5 border w-96 shadow-lg rounded-md">
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Approve Purchase Request</h3>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Comments (optional)
                                    </label>
                                    <TextInput
                                        variant="textarea"
                                        value={approvalData.comments}
                                        onChange={(e) => setApprovalData({ ...approvalData, comments: e.target.value })}
                                        placeholder="Add approval comments..."
                                        rows={3}
                                    />
                                </div>
                                <div className="flex items-center justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => setShowApprovalModal(false)}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleApprove}
                                        disabled={actionLoading === 'approve'}
                                        className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50"
                                    >
                                        {actionLoading === 'approve' ? 'Approving...' : 'Approve'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Rejection Modal */}
                {showRejectionModal && (
                    <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-[60]">
                        <div className="bg-white p-5 border w-96 shadow-lg rounded-md">
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Reject Purchase Request</h3>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Rejection Reason *
                                    </label>
                                    <TextInput
                                        variant="textarea"
                                        value={rejectionData.reason}
                                        onChange={(e) => setRejectionData({ ...rejectionData, reason: e.target.value })}
                                        placeholder="Please provide a reason for rejection..."
                                        rows={3}
                                        required
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Additional Comments
                                    </label>
                                    <TextInput
                                        variant="textarea"
                                        value={rejectionData.comments}
                                        onChange={(e) => setRejectionData({ ...rejectionData, comments: e.target.value })}
                                        placeholder="Add additional comments..."
                                        rows={2}
                                    />
                                </div>
                                <div className="flex items-center justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => setShowRejectionModal(false)}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleReject}
                                        disabled={actionLoading === 'reject' || !rejectionData.reason.trim()}
                                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                                    >
                                        {actionLoading === 'reject' ? 'Rejecting...' : 'Reject'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto">
            {formContent}
        </div>
    );
};

export default PurchaseRequestForm;